/**
 * Primitives shared by the field's hand-rolled SVGs.
 *
 * The plot and the four lens charts are built with direct DOM writes (no chart library —
 * see the port contract), so they all need the same handful of helpers.
 *
 * The old standalone `/velocity` screen (`./index.tsx`) is gone — this module, `plot.ts`,
 * `plot-math.ts` and `lenses.ts` now serve `@/features/views/field-view.tsx` and its siblings,
 * rendered inside the Scenarios screen's four-views rail (`@/features/sku-velocity/scenarios`).
 */

const SVG_NS = "http://www.w3.org/2000/svg";

/** Dx accent (cool neutral) — ties Dx nodes to the right-hand inventory axis. */
export const DXC = "#7C93A0";

/**
 * NPI treatment, shared by the canvas sprites and the legend swatch so the two can't drift.
 *
 * NPI is ~30% of a real portfolio, so a permanent halo on every one of them buries the
 * decision cohorts: NPI reads as `NPI_TINT`, a warm cast of the node's base colour, and the
 * gold ring is drawn only for the hovered node, the selection, or an active NPI cohort.
 */
export const NPI_TINT = "#BEAA80";
export const NPI_GOLD = "#C79A3B";

/** The field is always read at the current year; the year stepper lives on `/scenarios`. */
export const FIELD_YEAR = 2025;

export type Attrs = Record<string, string | number>;

export function el<K extends keyof SVGElementTagNameMap>(
  name: K,
  attrs: Attrs = {},
): SVGElementTagNameMap[K] {
  const e = document.createElementNS(SVG_NS, name) as SVGElementTagNameMap[K];
  for (const [k, v] of Object.entries(attrs)) {
    e.setAttribute(k, String(v));
  }
  return e;
}

export function svgText(attrs: Attrs, content: string): SVGTextElement {
  const t = el("text", attrs);
  t.textContent = content;
  return t;
}

/** Sankey ribbon: the edge `y1a→y1b` at `x1` widened/narrowed into `y2a→y2b` at `x2`. */
export function ribbon(
  x1: number,
  y1a: number,
  y1b: number,
  x2: number,
  y2a: number,
  y2b: number,
): string {
  const mx = (x1 + x2) / 2;
  return `M${x1} ${y1a} C${mx} ${y1a} ${mx} ${y2a} ${x2} ${y2a} L${x2} ${y2b} C${mx} ${y2b} ${mx} ${y1b} ${x1} ${y1b} Z`;
}

/**
 * Money formatting moved to `@/lib/data` so the agent's answers label the served reporting
 * currency the same way these widgets do. Re-exported here because the field imports it from
 * `./util` in a dozen places, and one symbol table is the point.
 */
export { curSym, fmtMoneyShort, moneyM } from "@/lib/data";

/** Boxes per month. Real velocity is fractional at the tail, so cap the decimals. */
export function fmtVel(v: number): string {
  return v.toLocaleString(undefined, { maximumFractionDigits: v < 10 ? 1 : 0 });
}

/**
 * Resolves one of the screen's static hosts by id.
 *
 * The ported engines write straight into the markup the field component renders, exactly as
 * the prototype did; the ids are part of that markup, so a miss is a programmer error.
 */
export function need<T extends Element = HTMLElement>(id: string): T {
  const e = document.getElementById(id);
  if (!e) {
    throw new Error(`[field] missing host #${id}`);
  }
  return e as unknown as T;
}
